import React, { useState } from 'react';
import { ConversationList } from './ConversationList';
import { ChatPanel } from './ChatPanel';
import { NotificationButton } from './NotificationButton';
import { useConversations } from '../hooks/useConversations';

export function Conversations() {
  const {
    conversations,
    activeConversationId,
    isMobile,
    isLoading,
    selectConversation,
    createNewConversation,
    sendMessage,
    deleteConversation,
    getActiveConversation
  } = useConversations();

  const [showChat, setShowChat] = useState(false);
  const activeConversation = getActiveConversation();

  const handleSelectConversation = (conversationId: string) => {
    selectConversation(conversationId);
    if (isMobile) {
      setShowChat(true);
    }
  };

  const handleNewConversation = () => {
    const newId = createNewConversation();
    if (isMobile) {
      setShowChat(true);
    }
  };

  const handleSendMessage = async (content: string) => {
    if (activeConversationId) {
      await sendMessage(activeConversationId, content);
    }
  };

  const handleBackToList = () => {
    setShowChat(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 mx-auto mb-4 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-muted-foreground">Loading conversations...</p>
        </div>
      </div>
    );
  }

  // Mobile Layout
  if (isMobile) {
    return (
      <div className="min-h-screen bg-background">
        {!showChat ? (
          // Show conversation list on mobile
          <div className="h-screen flex flex-col">
            {/* Mobile Header */}
            <div className="flex items-center justify-between p-4 border-b bg-background">
              <h1 className="text-xl font-bold">AI Chat</h1>
              <NotificationButton />
            </div>
            
            <ConversationList
              conversations={conversations}
              activeConversationId={activeConversationId}
              onSelectConversation={handleSelectConversation}
              onNewConversation={handleNewConversation}
              onDeleteConversation={deleteConversation}
              className="flex-1"
            />
          </div>
        ) : (
          // Show chat panel on mobile
          <div className="h-screen">
            <ChatPanel
              conversation={activeConversation}
              onSendMessage={handleSendMessage}
              onBack={handleBackToList}
              showBackButton={true}
              className="h-full"
            />
          </div>
        )}
      </div>
    );
  }

  // Desktop Layout (WhatsApp-style)
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/50">
      {/* Desktop Header */}
      <div className="border-b bg-background">
        <div className="max-w-7xl mx-auto flex items-center justify-between p-4">
          <h1 className="text-2xl font-bold">AI Chat Assistant</h1>
          <NotificationButton />
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto h-[calc(100vh-80px)]">
        <div className="flex h-full shadow-lg rounded-lg overflow-hidden bg-background border">
          {/* Sidebar - Conversations List */}
          <div className="w-80 flex-shrink-0">
            <ConversationList
              conversations={conversations}
              activeConversationId={activeConversationId}
              onSelectConversation={handleSelectConversation}
              onNewConversation={handleNewConversation}
              onDeleteConversation={deleteConversation}
              className="h-full"
            />
          </div>

          {/* Main Panel - Chat */}
          <div className="flex-1">
            <ChatPanel
              conversation={activeConversation}
              onSendMessage={handleSendMessage}
              className="h-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
}